/**
 * Loans & Business Financing — 10 documents.
 *
 * None of these entries carry a template, and that is deliberate. Financing documents are the
 * lender's paper: a bank, an equipment financier, an inventory lender or a revenue-share provider
 * hands you their form and expects it back signed. Your job is not to draft one — it is to
 * understand what you are being asked to sign, negotiate the handful of terms that are actually
 * negotiable, and put the security and guarantee documents in front of counsel before you sign
 * anything. These entries exist to make that conversation shorter and better informed.
 */
export const FINANCING_DOCS = [
  {
    id: "business-loan-agreement",
    name: "Business Loan Agreement",
    category: "financing",
    subcategory: "Loans & notes",
    aliases: ["Term Loan Agreement", "Commercial Loan Agreement", "Credit Agreement", "Bank Loan"],
    stages: ["manufacturing", "launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company (borrower) + Lender, usually with a founder guarantee alongside",
    whenYouNeedIt:
      "When you need cash the business will repay rather than ownership you can never buy back — funding a production run, a large purchase order, a co-packer deposit or a season's inventory build.",
    whatItDoes:
      "Sets the amount, the rate and the repayment schedule, and then — in the longer half of the document nobody reads — everything the company promises to do and not do while the loan is outstanding.",
    whyItMatters:
      "Founders read the rate and the repayment schedule and skim the covenants, which is where the loan actually lives. Reporting obligations, restrictions on further borrowing, limits on what you can do with your inventory and equipment, and the definition of default are all in there, and a brand that trips a covenant during a slow quarter is negotiating from a much weaker position than it occupied when it signed. This is the lender's document, drafted by the lender's counsel; the terms that matter to a CPG brand are usually the ones nobody thought to ask about.",
    majorTerms: [
      "Principal amount and drawdown mechanics",
      "Interest rate and how it is set",
      "Fees: origination, unused line, prepayment",
      "Repayment schedule and any interest-only period",
      "Term and maturity",
      "Collateral required",
      "Personal guarantee required",
      "Financial covenants and reporting obligations",
      "Restrictions on additional debt and on distributions",
      "Events of default and cure rights",
      "Conditions to funding",
    ],
    clausesToWatch: [
      {
        clause: "Financial covenants",
        why: "Covenants built for a business with even monthly revenue can be tripped by a seasonal brand in an ordinary quarter, with no operational failure behind it.",
      },
      {
        clause: "Events of default, including broad 'material adverse change' language",
        why: "Written broadly, it hands the lender an opening whenever they choose to use it, which is rarely a month you had spare cash.",
      },
      {
        clause: "Restrictions on additional debt",
        why: "A blanket restriction can block the inventory or purchase-order financing you need three months later to say yes to a chain authorisation.",
      },
      {
        clause: "Collateral description",
        why: "A broad description reaches inventory, receivables and equipment at once, and what that means for your ability to operate is a question for counsel before signing rather than after a missed payment.",
      },
      {
        clause: "Prepayment fees",
        why: "A penalty for repaying early removes the benefit of a strong season, which is exactly when you would want to clear the balance.",
      },
      {
        clause: "Cross-default provisions",
        why: "A default under one agreement can trigger defaults under others, so a dispute with an equipment financier becomes a problem with your bank.",
      },
      {
        clause: "Personal guarantee requirement",
        why: "It moves the exposure from the company to you personally, and it is usually presented as a formality at the end of the stack.",
      },
    ],
    cpgIssues: [
      "Seasonality is the core mismatch: covenants measured monthly or quarterly punish a brand whose revenue lands in one part of the year, even when the year as a whole is fine.",
      "Retailer payment terms mean cash is out for months, so a repayment schedule set on an optimistic collection assumption fails before anything else does.",
      "Deductions, chargebacks, spoils and free fill reduce what actually lands against the invoices a lender may be counting as your ability to repay.",
      "Restrictions on further borrowing can prevent the inventory or purchase-order financing that a large authorisation requires, which turns a growth opportunity into a covenant problem.",
      "A broad collateral description covers inventory sitting in a 3PL's warehouse, where the warehouse's own arrangements over goods it holds may sit alongside or against the lender's — a conflict to hand counsel before signing, not after the pallets move.",
      "Equipment bought with the loan may be equipment an equipment financier already has an interest in, and the two sets of paperwork need to be reconciled.",
    ],
    founderMistakes: [
      "Agreeing monthly covenants on a business that does most of its volume in one season, then breaching in a predictable February.",
      "Not realising a debt restriction blocked the purchase-order facility needed three months later for a chain launch.",
      "Personally guaranteeing with no cap and no release point because it was presented at closing as routine.",
      "Modelling repayment on invoiced revenue and not on collected revenue after deductions.",
      "Signing the lender's form with no negotiation at all on the assumption that banks do not negotiate.",
    ],
    negotiationPoints: [
      "Ask for covenants measured over a period that matches your season, with headroom at your weakest month.",
      "Ask for a carve-out permitting inventory, purchase-order and equipment financing up to a stated amount.",
      "Ask for written notice and a cure period before any default is called.",
      "Ask to cap and time-limit any personal guarantee, with a defined release trigger.",
      "Ask that the collateral description covers what the lender actually needs, not everything you own.",
      "Ask how the lender expects to deal with inventory held at a third-party warehouse, and start that conversation early.",
    ],
    redFlags: [
      "Covenants you cannot meet in your slowest month, agreed on the basis that it will be fine.",
      "A broadly written default provision with no notice and no cure period.",
      "A blanket collateral description nobody has explained to you line by line.",
      "An unlimited, open-ended personal guarantee.",
      "Prepayment penalties running the length of a long term.",
      "Cross-default reaching your co-packer or supplier agreements.",
    ],
    beforeYouSign: [
      "A twelve-month cash model that includes your real season, your retailer terms and your deduction history.",
      "A list of every existing lender, lessor and warehouse arrangement affecting your assets.",
      "Counsel's read of the covenants, the default provisions and the collateral description.",
      "The guarantee documents, read separately and with counsel rather than as part of the stack.",
    ],
    related: [
      "promissory-note",
      "line-of-credit-agreement",
      "security-agreement",
      "personal-guarantee",
      "inventory-financing-agreement",
      "three-pl-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promissory-note",
    name: "Promissory Note",
    category: "financing",
    subcategory: "Loans & notes",
    aliases: ["Note", "Loan Note", "Commercial Promissory Note", "IOU"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Borrower + Lender",
    whenYouNeedIt:
      "Whenever money is lent or borrowed and there is no longer-form loan agreement — a short bridge, a supplier advance, a family member funding a production run.",
    whatItDoes:
      "A short document recording the promise to repay: how much, at what interest, on what schedule, and what happens if a payment is missed.",
    whyItMatters:
      "This is the document most often replaced by a text message. In a brand's first three years small loans accumulate — a co-packer deposit funded by a relative, a bridge from a board member, an advance from an early stockist — and if none of them are papered, nobody can say what the company actually owes. A future lender, investor or buyer will ask for the complete list, and an undocumented loan is worse than a documented one: it is a liability with no agreed terms and a relationship attached to it.",
    majorTerms: [
      "Principal amount and what it funds",
      "Interest rate",
      "Payment schedule and any interest-only period",
      "Maturity date",
      "Prepayment rights",
      "Events of default and acceleration",
      "Late fees and default interest",
      "Security over company assets, if any",
      "Guarantee, if any",
      "Restrictions on assignment",
    ],
    clausesToWatch: [
      {
        clause: "Acceleration on default",
        why: "Immediate acceleration on one missed payment turns a short cash gap into a demand for the whole balance in the month you had least of it.",
      },
      {
        clause: "Security over company assets",
        why: "A secured note reaches your inventory and receivables, and what that means for moving product and taking further financing is a question for counsel before signing.",
      },
      {
        clause: "Assignment",
        why: "A note sold on to a third party puts a stranger in the lender's seat with the same rights and none of the goodwill.",
      },
      {
        clause: "Default interest and late fees",
        why: "Rates that only apply after a stumble are the ones founders never model, and they compound during exactly the period you can least afford them.",
      },
      {
        clause: "Personal guarantee",
        why: "A guarantee attached to a small note moves the exposure out of the company and into your own life, where it can outlast the business.",
      },
      {
        clause: "Prepayment",
        why: "Without an express right to prepay without penalty, a good season does not let you clear the debt.",
      },
    ],
    cpgIssues: [
      "Repayment schedules rarely match the cash conversion cycle: the co-packer is paid before production and the retailer pays long after delivery, so repayment starting at production is repayment out of nothing.",
      "A note funding one production run should be sized and timed to that run's sell-through rather than to a calendar that ignores your season.",
      "A lender who is also your ingredient supplier, freight broker or distributor now holds two relationships with you, and they pull against each other the first time a shipment is late.",
      "Several small notes taken across one season quietly become the company's largest liability, and no single document shows the total.",
      "Notes taken to cover a retailer's deduction shortfall tend to repeat every season, and without a limit they become a permanent funding line at the worst possible price.",
    ],
    founderMistakes: [
      "Borrowing from family with nothing in writing and later arguing over whether it was a loan or an investment.",
      "Agreeing repayment thirty days after production when the retailer paid in ninety.",
      "Missing a single payment on a note with immediate acceleration and no cure period.",
      "Letting notes accumulate with no central record of balances, rates or maturity dates.",
    ],
    negotiationPoints: [
      "Ask for a repayment schedule tied to when the funded inventory actually converts to cash.",
      "Ask for a grace period and written notice before any default is declared.",
      "Ask to prepay in full without penalty.",
      "Ask that the note is unsecured and carries no personal guarantee.",
      "Ask that it cannot be assigned to a third party without your written consent.",
    ],
    redFlags: [
      "No written document at all.",
      "Acceleration with no notice and no cure period.",
      "A personal guarantee attached to a modest loan.",
      "Free assignment to anyone the lender chooses.",
      "Default interest at a level you have never modelled.",
    ],
    beforeYouSign: [
      "A thirteen-week cash view showing when repayment is genuinely possible.",
      "The complete list of the company's existing debts and who holds them.",
      "Counsel's read if any security or guarantee is attached.",
    ],
    related: [
      "business-loan-agreement",
      "founder-loan-agreement",
      "shareholder-loan-agreement",
      "promissory-note-investor",
      "security-agreement",
      "personal-guarantee",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-loan-agreement",
    name: "Founder Loan Agreement",
    category: "financing",
    subcategory: "Insider loans",
    aliases: ["Founder Loan", "Owner Loan", "Director Loan", "Founder Advance"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Founder (in their personal capacity)",
    whenYouNeedIt:
      "When a founder puts personal money into the company — covering a co-packer deposit, a can order, a slotting payment or payroll — and it is meant to come back rather than buy more ownership.",
    whatItDoes:
      "Documents the founder's money as a loan to the company: the amount, what it funded, whether interest accrues, when it is repayable, and where it ranks against everyone else the company owes.",
    whyItMatters:
      "Almost every CPG founder funds something personally in the first two years, and it is almost always inventory. If it is never documented, the money is invisible: it does not appear on the cap table, it does not appear as a liability, and when a co-founder relationship sours or a buyer arrives there is no record that it was ever a loan rather than a gift. Investors also look closely at founder loans, because repaying one out of new investment is not what the money was raised for — so decide the treatment now, in writing, rather than in the middle of a round.",
    majorTerms: [
      "Amount and specifically what it funded",
      "Interest, if any",
      "Repayment trigger and schedule",
      "Subordination to other lenders",
      "Board or member approval of the loan",
      "Whether repayment is blocked while other debt is outstanding",
      "Whether conversion to equity is contemplated, and on what terms",
      "Treatment on a sale of the company",
    ],
    clausesToWatch: [
      {
        clause: "Approval by the board or the other members",
        why: "A founder lending to their own company is a related-party transaction, and without a proper approval it becomes a diligence finding and sometimes a dispute between founders.",
      },
      {
        clause: "Subordination",
        why: "A future lender will normally require this loan to sit behind theirs, and agreeing that now is far easier than agreeing it when a facility is on the table.",
      },
      {
        clause: "Repayment trigger",
        why: "'When there is cash' is not a trigger; it becomes a judgement call the founder makes about their own repayment, which is exactly what investors object to.",
      },
      {
        clause: "Conversion language",
        why: "A vague expectation that the loan will 'turn into equity someday' creates an argument about price at the next round.",
      },
      {
        clause: "Interest rate on a related-party loan",
        why: "An unusual rate raises questions for your accountant and for anyone reviewing the company later, so pick it deliberately and ask first.",
      },
    ],
    cpgIssues: [
      "Founder money most often funds a can or label minimum, a co-packer deposit or a slotting payment — write down exactly what it funded, because that record is the evidence it was a loan.",
      "A founder loan taken to cover a retailer's deduction shortfall tends to repeat every season, so it needs a cap and a plan rather than an open habit.",
      "Investors frequently require founder loans to be repaid, converted or subordinated as a condition of a round, and an undocumented loan cannot be any of those things cleanly.",
      "Where one founder funds production and another does not, an undocumented loan becomes an equity argument years later, usually at the term sheet.",
      "A founder loan sitting behind a bank or inventory lender's arrangements may not be repayable at the moment the founder needs the money back.",
    ],
    founderMistakes: [
      "Funding three production runs on personal cards with no record of any of it.",
      "Repaying themselves ahead of suppliers during a cash squeeze, which then had to be explained in diligence.",
      "Converting the loan to equity informally at a number nobody documented or approved.",
      "Never disclosing the loan to the other founders, who learned about it from the buyer's accountants.",
    ],
    negotiationPoints: [
      "Ask that the loan is approved by the board or the other members and recorded properly.",
      "Ask for a repayment trigger tied to a real, observable event rather than to available cash.",
      "Ask that subordination terms are written now, before a lender demands them under time pressure.",
      "Ask your accountant about the interest rate before choosing one.",
      "Ask that the description of what the money funded is specific enough to stand up years later.",
    ],
    redFlags: [
      "Money moved with no document and no approval.",
      "Repayment to a founder ahead of trade creditors during a shortfall.",
      "An informal promise of extra equity in exchange for the loan.",
      "No disclosure to co-founders or to existing investors.",
      "A founder loan repaid immediately out of new investment.",
    ],
    beforeYouSign: [
      "Bank records showing exactly what was funded and when.",
      "The other founders' or members' written approval of the loan.",
      "A view of every existing lender's restrictions on repaying insiders.",
    ],
    related: [
      "shareholder-loan-agreement",
      "promissory-note",
      "cap-table-documentation",
      "llc-operating-agreement",
      "board-consent",
      "business-loan-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "shareholder-loan-agreement",
    name: "Shareholder Loan Agreement",
    category: "financing",
    subcategory: "Insider loans",
    aliases: ["Member Loan Agreement", "Investor Bridge Loan", "Related-Party Loan"],
    stages: ["launch", "growth", "fundraising"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Existing Shareholder or Member",
    whenYouNeedIt:
      "When an existing shareholder or member lends the company money rather than buying more of it — often a bridge before a round, or a seasonal top-up to fund a build.",
    whatItDoes:
      "Records the loan and, critically, where it sits relative to everyone else the company owes, whether it can convert into ownership, and who approved it.",
    whyItMatters:
      "A shareholder who is also a lender holds two positions in the company and they pull in opposite directions: as a shareholder they want the company to invest for growth, as a lender they want to be repaid. The usual problem is not the money but the absence of a decision about ranking and conversion, which then has to be made at the moment the company has least leverage — a delayed round, a slow season, an acquisition on the table. Deciding it while everyone is relaxed costs nothing; deciding it later costs a great deal.",
    majorTerms: [
      "Amount and purpose",
      "Interest rate",
      "Repayment trigger and maturity",
      "Subordination to other lenders",
      "Conversion rights, if any, and the price",
      "Approval by directors or members with no interest in the loan",
      "Security over company assets, if any",
      "Treatment on a sale of the company",
      "Restrictions on assignment",
    ],
    clausesToWatch: [
      {
        clause: "Conversion rights",
        why: "A loan that can convert at the lender's choice, on terms fixed now, can become extremely valuable to them and extremely expensive to you at the next round.",
      },
      {
        clause: "Repayment on demand",
        why: "A shareholder able to demand repayment at will can do so during the season when all your cash is sitting in inventory.",
      },
      {
        clause: "Security over company assets",
        why: "A secured shareholder sits ahead of every other shareholder in a difficult outcome, which is rarely what the other holders understood.",
      },
      {
        clause: "Approval by disinterested directors or members",
        why: "Without it the loan is a related-party transaction nobody independent signed off, and that is a diligence finding in every subsequent process.",
      },
      {
        clause: "Subordination",
        why: "An incoming lender will normally require this loan behind theirs, and a shareholder who refuses at that point can block the facility you need.",
      },
    ],
    cpgIssues: [
      "A shareholder who is also your distributor, broker or co-packer becomes a creditor of a company they trade with, and that conflict surfaces the first time there is a quality dispute or a termination.",
      "Seasonal top-ups from the same shareholder repeat year after year and quietly become the company's main funding line, at terms nobody ever revisited.",
      "A demand-repayable shareholder loan during a build season, when cash is entirely tied up in cans and finished goods, is a genuine solvency risk rather than an inconvenience.",
      "Incoming investors usually require shareholder debt to be converted, subordinated or repaid, so the decision is coming whether you make it now or later.",
      "A shareholder loan secured against inventory changes the ranking of everyone else in an exit, including the founders.",
    ],
    founderMistakes: [
      "Taking a repeat seasonal loan from the same shareholder with no cap, no schedule and no approval.",
      "Agreeing conversion terms verbally and discovering the lender's version at the next round.",
      "Letting a shareholder loan be secured against inventory without understanding the consequences.",
      "Not obtaining disinterested approval, which then became a condition to closing a raise.",
      "Repaying a shareholder ahead of suppliers during a squeeze.",
    ],
    negotiationPoints: [
      "Ask for a fixed maturity rather than repayment on demand.",
      "Ask that any conversion is on defined terms agreed now, in writing.",
      "Ask for subordination language a future lender will actually accept.",
      "Ask that the loan is approved by people who do not benefit from it.",
      "Ask that the lender's shareholder rights and lender rights are kept separate and stated separately.",
    ],
    redFlags: [
      "Repayment on demand.",
      "Security over inventory or receivables held by a shareholder.",
      "Conversion at the lender's option with no defined price.",
      "No independent approval of the transaction.",
      "A lender who is also a supplier linking repayment to supply terms.",
    ],
    beforeYouSign: [
      "Every existing lender's restrictions on repaying insiders.",
      "Written approvals from directors or members with no interest in the loan.",
      "A clear statement of what happens to the loan on a sale of the company.",
    ],
    related: [
      "founder-loan-agreement",
      "promissory-note",
      "convertible-note",
      "security-agreement",
      "shareholder-agreement",
      "cap-table-documentation",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "line-of-credit-agreement",
    name: "Line of Credit Agreement",
    category: "financing",
    subcategory: "Working capital",
    aliases: ["Revolving Credit Facility", "Revolver", "Working Capital Line", "Operating Line"],
    stages: ["manufacturing", "launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company (borrower) + Lender",
    whenYouNeedIt:
      "When your problem is timing rather than losses — you pay for cans, ingredients and production months before a retailer or distributor pays you.",
    whatItDoes:
      "Gives you a limit you can draw against and repay repeatedly, with interest on what is drawn, subject to conditions the lender re-tests every time you want money.",
    whyItMatters:
      "A revolving line is the closest thing to the right product for a CPG brand's actual problem: money out at production, money in ninety days after delivery. The trap is that availability is not the limit. Lenders decide what you may actually draw by applying a formula to your inventory and receivables, and that formula excludes exactly what a brand has plenty of — slow-moving SKUs, short-dated stock, concentration in one large retailer, and receivables reduced by deductions. Founders plan against the headline number and meet the real one in the month they need it most.",
    majorTerms: [
      "Facility limit",
      "Borrowing base or availability formula",
      "Advance rates against inventory and against receivables",
      "Eligibility exclusions",
      "Interest and unused-line fees",
      "Reporting frequency and stock reporting",
      "Clean-down requirement",
      "Financial covenants",
      "Collateral and any guarantee",
      "Term, renewal and termination rights",
    ],
    clausesToWatch: [
      {
        clause: "Borrowing base definitions and eligibility exclusions",
        why: "This is the clause that decides your actual availability, and it routinely excludes short-dated, slow-moving or third-party-held stock that makes up most of a brand's inventory.",
      },
      {
        clause: "Concentration limits",
        why: "A cap on how much of the base can come from one customer penalises you for winning a large chain, so success can reduce your borrowing power.",
      },
      {
        clause: "Clean-down requirement",
        why: "A requirement to hold a zero balance for a period each year can be impossible for a brand with a single selling season, and breaching it is a default.",
      },
      {
        clause: "The lender's discretion to change advance rates",
        why: "Unilateral adjustment mid-season can cut your availability at the exact moment you have committed to a production run.",
      },
      {
        clause: "Requirements involving your warehouse or 3PL",
        why: "Inventory the lender cannot get comfortable about often does not count at all, and sorting that out after signing is much harder than before.",
      },
      {
        clause: "Demand or short-notice termination features",
        why: "A facility that can be pulled quickly is not the working capital base you thought you were building a season around.",
      },
    ],
    cpgIssues: [
      "Short-dated and slow-moving inventory is commonly excluded from the borrowing base, so for a summer beverage brand the stock you hold in February may count for very little.",
      "Retailer deductions, spoils allowances and free fill reduce receivables after invoicing, which means the collateral shrinks after you have already drawn against it.",
      "Concentration limits hit hardest right after your best news: one chain becoming most of your business reduces the proportion of your receivables that qualify.",
      "Inventory held at a 3PL or sitting at a co-packer often needs the third party's cooperation before a lender will count it, and the warehouse's own arrangements over goods it holds may conflict — that overlap is counsel's to resolve.",
      "A clean-down period designed for a business with even monthly sales can be unachievable for a seasonal brand, and lenders do not always adjust it unless asked.",
    ],
    founderMistakes: [
      "Planning a production run against the headline limit and finding the borrowing base supported a fraction of it.",
      "Not realising deductions would reduce eligible receivables after the draw was already made.",
      "Failing a clean-down requirement in the first year because the season did not allow it.",
      "Never arranging the paperwork with the 3PL, so warehouse inventory was excluded from the base entirely.",
      "Treating the line as permanent when it was renewable annually at the lender's discretion.",
    ],
    negotiationPoints: [
      "Ask for the borrowing base formula in writing, with worked examples using your own numbers from your slowest and busiest months.",
      "Ask for concentration limits that reflect the reality of chain retail rather than a generic assumption.",
      "Ask for a clean-down window that falls after your season, or for none at all.",
      "Ask that advance rates cannot be changed without notice and a stated reason.",
      "Ask what the lender needs from your 3PL and co-packer, and start those conversations before you sign.",
    ],
    redFlags: [
      "A facility limit quoted with no borrowing base explanation.",
      "Eligibility exclusions you have not tested against your actual stock list.",
      "A unilateral right to change advance rates or demand repayment.",
      "A clean-down period falling inside your build season.",
      "A guarantee added quietly at the end of the process.",
    ],
    beforeYouSign: [
      "Your own borrowing-base calculation using last year's inventory and receivables, month by month.",
      "Your deduction and chargeback history by retailer.",
      "Your 3PL agreement and whatever the lender needs from the warehouse.",
      "Counsel's read of the collateral and guarantee documents, separately from the facility itself.",
    ],
    related: [
      "inventory-financing-agreement",
      "business-loan-agreement",
      "security-agreement",
      "personal-guarantee",
      "three-pl-agreement",
      "retail-vendor-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "equipment-financing-agreement",
    name: "Equipment Financing Agreement",
    category: "financing",
    subcategory: "Asset finance",
    aliases: ["Equipment Lease", "Equipment Loan", "Capital Lease", "Equipment Finance Agreement"],
    stages: ["manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Equipment Financier or Lessor, usually with a founder guarantee",
    whenYouNeedIt:
      "When you buy or lease production equipment — a filling line, a labeller, a depalletiser, cold storage, a delivery vehicle — and the financier's paperwork comes with it.",
    whatItDoes:
      "Funds the equipment and takes an interest in it until you have paid, with terms covering insurance, maintenance, where the machine may sit, whether you may move it, and what happens at the end of the term.",
    whyItMatters:
      "Equipment finance is the easiest money a young brand can get, because the machine itself backs the deal — which is also why the terms are the financier's and are presented as non-negotiable. Two things catch founders. The equipment is frequently outgrown long before the term ends, and the agreement may restrict moving it, subleasing it or selling it. And a personal guarantee is almost always attached to a small brand's first machine. What the financier's interest in that equipment allows them to do is a question for counsel before you sign, not after a missed payment.",
    majorTerms: [
      "Amount financed and the equipment described",
      "Term and payment schedule",
      "Rate or implicit rate",
      "End-of-term options: purchase, return or renew",
      "Insurance requirements",
      "Maintenance and service obligations",
      "Permission to relocate the equipment",
      "Restrictions on sale or sublease",
      "Personal guarantee",
      "Who bears loss or damage",
      "Default and the financier's remedies",
    ],
    clausesToWatch: [
      {
        clause: "End-of-term purchase price",
        why: "A nominal buyout and a fair-market buyout are completely different deals, and the difference is usually not obvious from the payment schedule.",
      },
      {
        clause: "Relocation restrictions",
        why: "If you move co-packers, the machine has to move too, and needing consent from a financier during a production transition is leverage you handed away at signing.",
      },
      {
        clause: "Risk of loss and insurance",
        why: "You normally carry the risk of damage even while the financier holds the interest, and the required limits may exceed what your current policy carries.",
      },
      {
        clause: "Cross-collateralisation",
        why: "It can tie this machine to other agreements with the same financier, so a dispute about one asset reaches equipment you had already paid down.",
      },
      {
        clause: "Early termination cost",
        why: "Outgrowing the equipment is the likely outcome, and an undisclosed early-exit figure decides whether upgrading is affordable.",
      },
      {
        clause: "Maintenance tied to a named service provider",
        why: "An obligation to service only through one provider, at unstated rates, is an open-ended cost on top of the payments.",
      },
      {
        clause: "Personal guarantee",
        why: "It keeps running against you personally even after the equipment is long gone from the business.",
      },
    ],
    cpgIssues: [
      "Brands outgrow their first filling or labelling equipment quickly: a line sized for a few thousand cases a month becomes an obstacle at scale, and the finance term routinely outlasts the machine's usefulness to you.",
      "Equipment installed at a co-packer's facility raises the question of whose it is and who may move it, and the co-packer's own arrangements over what sits in their plant may conflict with the financier's — get that squared in writing before installation.",
      "Equipment specific to one pack format loses value fast if you change can size or move to a pouch, which matters a great deal under a fair-market end-of-term option.",
      "Cold storage and refrigerated vehicles carry downtime consequences that land on product rather than on the asset: a failure is a spoiled load, not just a repair bill.",
      "A financier's insurance requirements may exceed your current cover, and product liability is a separate policy question that the equipment agreement will not address.",
    ],
    founderMistakes: [
      "Signing a five-year term on a machine that was outgrown in eighteen months.",
      "Not reading the end-of-term option and discovering the buyout was at market value.",
      "Installing financed equipment in a co-packer's plant with no written acknowledgement from the co-packer.",
      "Personally guaranteeing a first machine and still carrying the exposure long after the equipment left the business.",
      "Not carrying the insurance the agreement required, which was itself a default.",
    ],
    negotiationPoints: [
      "Ask for a term no longer than your realistic use of the equipment, with an early buyout figure stated now.",
      "Ask for a fixed, stated end-of-term purchase option rather than a market-value one.",
      "Ask for permission to relocate the equipment on notice rather than on consent.",
      "Ask to cap and time-limit any personal guarantee.",
      "Ask that the financier's position is reconciled with your co-packer or landlord in writing before installation.",
    ],
    redFlags: [
      "An unlimited personal guarantee on a modest machine.",
      "No stated end-of-term option at all.",
      "Cross-collateralisation reaching equipment or agreements you did not intend to pledge.",
      "An outright prohibition on moving the equipment.",
      "Service obligations through a named provider at unstated rates.",
    ],
    beforeYouSign: [
      "A realistic view of what volume this equipment supports and when you will outgrow it.",
      "Written acknowledgement from whoever owns or controls the building the equipment will sit in.",
      "Your insurance certificate checked against the agreement's requirements.",
      "The guarantee documents, read separately with counsel.",
    ],
    related: [
      "personal-guarantee",
      "security-agreement",
      "business-loan-agreement",
      "co-packer-agreement",
      "warehouse-agreement",
      "promissory-note",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The financier takes an interest in the equipment and almost always a personal guarantee from you, and what each of those means in practice is exactly what counsel is for.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "inventory-financing-agreement",
    name: "Inventory Financing Agreement",
    category: "financing",
    subcategory: "Working capital",
    aliases: ["Inventory Line", "Purchase Order Financing", "Asset-Based Lending", "Trade Finance Facility"],
    stages: ["manufacturing", "launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Inventory or Purchase-Order Financier",
    whenYouNeedIt:
      "When you have demand you cannot fund — a chain authorisation, a large distributor order, a season to build for — and every dollar you have is already in cans, ingredients and finished goods.",
    whatItDoes:
      "Advances money against inventory, or against a confirmed purchase order, so you can produce before you are paid. The financier takes an interest in the goods and usually in the receivable that follows them.",
    whyItMatters:
      "Inventory and receivables are the two things a CPG brand always needs financed, and both are lumpy: you buy a full pallet minimum of cans, run a full production batch, then wait out a retailer's payment terms. Used well, this is the money that lets you say yes to a chain without selling ownership. Used carelessly, it is expensive, and it puts a financier's paperwork over goods physically sitting in someone else's warehouse, alongside whatever your 3PL, your co-packer and your other lenders already assume about that same inventory. Every one of those overlaps is a question for counsel before the first draw.",
    majorTerms: [
      "Facility size and advance rate",
      "What inventory qualifies and what is excluded",
      "Purchase-order financing mechanics, where included",
      "Fees and the total cost of funding a run",
      "Repayment trigger, usually collection of the receivable",
      "Inspection, audit and stock-count rights",
      "Reporting obligations",
      "Required arrangements with the warehouse or 3PL",
      "Collateral description",
      "Guarantee, if any",
      "Term and termination",
    ],
    clausesToWatch: [
      {
        clause: "Exclusions from eligible inventory",
        why: "Short-dated, slow-moving, seasonal or third-party-held stock is commonly excluded, and for many brands that is most of what is in the warehouse.",
      },
      {
        clause: "The true cost once fees are added to the rate",
        why: "A modest-sounding rate plus per-draw and per-run fees can make funding a single production batch far more expensive than any comparison you were shown.",
      },
      {
        clause: "Arrangements required with your warehouse or 3PL",
        why: "The financier's position over goods held by a third party depends on paperwork the third party has to sign, and that conversation is much harder after you have drawn.",
      },
      {
        clause: "Repayment trigger",
        why: "If repayment assumes the invoice is collected in full, retailer deductions and late payment leave a gap you have to fund from somewhere else.",
      },
      {
        clause: "Cross-default with your other facilities",
        why: "A stumble here can put your bank line into default, so the two agreements have to be read against each other.",
      },
      {
        clause: "Termination and repayment on short notice",
        why: "A facility that can be withdrawn quickly leaves you mid-season with inventory financed and no line behind it.",
      },
    ],
    cpgIssues: [
      "Inventory in a 3PL's warehouse is physically controlled by the 3PL, and the warehouse's own arrangements over goods it holds sit alongside — and sometimes against — the financier's; untangling that is counsel's job and is best done before the pallets move.",
      "Short shelf life makes inventory a depreciating asset, and financiers commonly exclude anything within a stated window of its date, which for fresh or refrigerated products can be most of the stock.",
      "Retailer deductions, spoils and free fill mean the receivable that repays the facility is smaller than the invoice that created it.",
      "Seasonal builds mean you carry maximum inventory at the moment your sales are lowest, which is exactly when advance rates and eligibility are least generous.",
      "Product held at a co-packer awaiting release, or sitting in transit, is often not eligible at all — which is where a large share of a brand's value sits in the weeks before a launch.",
    ],
    founderMistakes: [
      "Modelling the facility on the headline advance rate and ignoring the fees, so the true cost of funding one run was far above expectation.",
      "Not arranging the paperwork with the 3PL, so the inventory never qualified and the facility was unusable.",
      "Financing a production run for a retailer that then cut the order.",
      "Assuming a deduction-heavy receivable would repay the draw in full.",
      "Taking inventory finance while an existing bank facility already restricted additional borrowing.",
    ],
    negotiationPoints: [
      "Ask for the full cost expressed as total dollars to fund one production run, not as a rate.",
      "Ask which of your SKUs and which locations actually qualify, tested against your current stock list.",
      "Ask what the financier needs from your 3PL and co-packer, and get it agreed before you sign.",
      "Ask for a repayment mechanism that tolerates normal retailer deductions.",
      "Ask for a defined process if a retailer cancels an order you have already financed.",
    ],
    redFlags: [
      "Eligibility rules you have not tested against real stock.",
      "No clarity on how inventory at a third-party warehouse will be treated.",
      "Fees disclosed only as percentages, with no worked example.",
      "Cross-default reaching your other facilities.",
      "A personal guarantee introduced late in the process.",
      "A facility terminable on short notice with immediate repayment.",
    ],
    beforeYouSign: [
      "A SKU-level view of your inventory with date codes and locations.",
      "Your 3PL and co-packer agreements, read for anything touching goods they hold.",
      "Your deduction history by retailer.",
      "Every existing lender's restrictions on additional borrowing.",
      "Counsel's read of the collateral, warehouse and guarantee documents.",
    ],
    related: [
      "line-of-credit-agreement",
      "security-agreement",
      "personal-guarantee",
      "three-pl-agreement",
      "warehouse-agreement",
      "retail-vendor-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This financing puts a lender's paperwork over inventory that a warehouse, a co-packer and possibly another lender also have arrangements about, and only counsel can untangle that overlap before you draw.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "revenue-based-financing-agreement",
    name: "Revenue-Based Financing Agreement",
    category: "financing",
    subcategory: "Working capital",
    aliases: ["RBF", "Revenue Share Financing", "Royalty-Based Financing", "Merchant Cash Advance"],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Financing Provider",
    whenYouNeedIt:
      "When a provider offers cash today against a share of future sales, usually pitched as founder-friendly because there is no valuation, no board seat and no dilution.",
    whatItDoes:
      "Advances a sum that is repaid as a percentage of revenue until a fixed total is reached, so the size of each payment moves with sales but the total amount owed does not.",
    whyItMatters:
      "The pitch is that repayment flexes with your sales. The part that does not flex is the total: you owe a fixed multiple of what you took, so if sales come in slower than the model you pay for longer and at a higher effective cost. For a seasonal brand this matters enormously — a facility priced against a summer beverage season looks cheap in July and punishing from October through April, when the same percentage comes out of much smaller revenue. Convert any offer into a total dollar cost and an implied annual rate before you compare it with anything else.",
    majorTerms: [
      "Amount advanced",
      "Total repayment amount or multiple",
      "Revenue share percentage",
      "How revenue is defined and measured",
      "Payment frequency and collection method",
      "Minimum monthly payment, if any",
      "Outside date or term cap",
      "Reporting and data access",
      "Security or guarantee, if any",
      "Restrictions on other financing",
      "Early repayment terms",
    ],
    clausesToWatch: [
      {
        clause: "How revenue is defined",
        why: "Gross sales before deductions, returns, spoils and trade spend is a very different number from what you actually collect, and you may be sharing revenue that never reached you.",
      },
      {
        clause: "Minimum monthly payment",
        why: "It removes the flexibility the product is sold on, and it usually bites in your slowest month rather than your best one.",
      },
      {
        clause: "Direct access to your sales platform or bank account",
        why: "Automatic collection against a single account collides with the lumpy timing of retailer and distributor payments.",
      },
      {
        clause: "Restrictions on other financing",
        why: "They can block the inventory or purchase-order facility that would have funded the same production run far more cheaply.",
      },
      {
        clause: "Outside date requiring full repayment",
        why: "A hard date turns a revenue-linked obligation into a fixed one exactly when sales have underperformed.",
      },
      {
        clause: "Early repayment terms",
        why: "If the fixed total is not discounted for early repayment, a good season buys you nothing.",
      },
    ],
    cpgIssues: [
      "Seasonality is the whole issue: a share of revenue that is comfortable during a summer beverage season is heavy in the off-season, and the total owed does not shrink to match.",
      "Revenue measured on gross shipments ignores deductions, chargebacks and spoils allowances, so you can be sharing revenue that came back as a deduction two months later.",
      "A brand shifting channel mix — from direct-to-consumer into distributor sales at wholesale prices — sees revenue and margin move in opposite directions while the share percentage stays fixed.",
      "Collection by direct debit assumes steady daily receipts, which is a poor description of a brand paid by three distributors on their own schedules.",
      "Taking this financing can block the inventory or purchase-order financing that a large authorisation actually calls for, so consider the sequence before the first offer.",
    ],
    founderMistakes: [
      "Comparing a revenue share percentage to a bank rate without converting either into a dollar cost.",
      "Agreeing a share of gross revenue and paying on invoices that came back as deductions.",
      "Taking the advance just before a slow season and paying it down over eighteen months instead of six.",
      "Not noticing a minimum monthly payment set against a strong month.",
      "Taking two of these facilities from different providers within the same year.",
    ],
    negotiationPoints: [
      "Ask for the total dollar cost and the implied annual cost at a realistic sales pace, not the pitched one.",
      "Ask that revenue is defined net of returns, deductions and trade spend.",
      "Ask for no minimum monthly payment, or one you can meet in your slowest month.",
      "Ask for a discount on the total for early repayment.",
      "Ask that the agreement permits the inventory or purchase-order financing you may need for a large order.",
    ],
    redFlags: [
      "Revenue defined on gross shipments with no adjustment for deductions.",
      "A minimum payment set against your best month.",
      "Direct account access with no cap on what can be taken.",
      "Restrictions preventing any other financing at all.",
      "A personal guarantee attached to a product marketed as non-dilutive and unsecured.",
      "A same-day offer with pressure to sign before you can model it.",
    ],
    beforeYouSign: [
      "Two years of monthly revenue showing your actual seasonality.",
      "Your deduction and returns history.",
      "The offer converted into total dollars and an implied annual rate.",
      "A comparison against inventory or purchase-order financing for the same use of funds.",
      "Counsel's read if any guarantee or security is attached.",
    ],
    related: [
      "inventory-financing-agreement",
      "line-of-credit-agreement",
      "business-loan-agreement",
      "security-agreement",
      "personal-guarantee",
      "retail-vendor-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "security-agreement",
    name: "Security Agreement",
    category: "financing",
    subcategory: "Security & guarantees",
    aliases: ["General Security Agreement", "Collateral Agreement", "Pledge Agreement"],
    stages: ["manufacturing", "launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Lender or Financier",
    whenYouNeedIt:
      "Alongside almost any secured borrowing — it is the document that identifies which of the company's assets stand behind the debt.",
    whatItDoes:
      "Describes the assets the lender's arrangement attaches to, sets out what you may and may not do with those assets while the debt is outstanding, and states what the lender is entitled to do if you default.",
    whyItMatters:
      "This is the shortest document with the largest consequences in a financing package, and it is the one founders skim on the way to signing. For a brand, the assets described are usually everything the business consists of: inventory, receivables, equipment, and often the trademark and the formula. What the lender may actually do with those rights, and what the restrictions mean for your ability to move inventory, change 3PLs or add a co-packer, is a question to put to counsel before signing rather than during a slow quarter. Founders also miss that a broad description can foreclose the next financing entirely, because a second lender may not want to sit behind the first.",
    majorTerms: [
      "Description of the collateral",
      "Whether the description is blanket or specific",
      "Which obligations are secured — this loan, or everything you ever owe this lender",
      "Restrictions on selling, moving or further encumbering the assets",
      "Insurance and record-keeping obligations",
      "Inspection rights and frequency",
      "Permitted locations for inventory",
      "Requirements involving third parties holding your goods",
      "Events of default",
      "The lender's remedies",
    ],
    clausesToWatch: [
      {
        clause: "Whether intellectual property is included",
        why: "Putting the trademark, the formula and the artwork behind a working capital loan means your two most valuable assets stand behind your least strategic debt, and a buyer will find it.",
      },
      {
        clause: "After-acquired inventory and future receivables",
        why: "A description reaching everything you will ever own means each new production run is captured automatically, including runs funded by someone else.",
      },
      {
        clause: "'All obligations now or hereafter owing'",
        why: "It ties the same collateral to future debts you have not agreed yet, so a small facility today secures a much larger one later without a fresh decision.",
      },
      {
        clause: "Permitted locations for inventory",
        why: "A location covenant conflicts with moving to a new 3PL or adding a second co-packer, and breaching it is a default even when nothing has gone wrong commercially.",
      },
      {
        clause: "Required cooperation from your 3PL or co-packer",
        why: "The lender's comfort depends on paperwork a third party has to sign, and that party has their own arrangements over goods they hold.",
      },
      {
        clause: "Inspection rights and reporting",
        why: "Stock counts and dating reports at a frequency your 3PL cannot produce become a breach you did not know you had agreed to.",
      },
    ],
    cpgIssues: [
      "Including the trademark and formula in a lender's collateral is common and rarely deliberate — brands do it to fund one production run and only discover it when a buyer's counsel maps the company's assets.",
      "Location covenants restrict which warehouse your stock sits in, which collides directly with moving 3PLs or adding a co-packer to serve a second region.",
      "The warehouse holding your goods has its own arrangements over what it stores, and where those meet the lender's paperwork is a genuine conflict for counsel rather than something to resolve by email.",
      "Short shelf life means the collateral expires, so lenders ask for dating reports and cycle counts your 3PL was never set up to produce.",
      "A blanket description can prevent the equipment or purchase-order financing you need for the next run, because the second financier has nothing left to take comfort in.",
    ],
    founderMistakes: [
      "Granting a blanket description including the trademark in order to fund a single production run.",
      "Agreeing a location covenant and then moving 3PLs without telling the lender.",
      "Learning only later that a second lender declined because the first had already taken everything.",
      "Agreeing inventory reporting obligations the 3PL was never asked to support.",
      "Signing without counsel because it was described as the standard security document.",
    ],
    negotiationPoints: [
      "Ask to carve intellectual property out of the collateral, or at minimum the trademark.",
      "Ask that the description secures this loan only, not all present and future obligations.",
      "Ask for permission to move inventory between named warehouses on notice.",
      "Ask for reporting your 3PL can genuinely produce, at a frequency you have confirmed with them.",
      "Ask counsel to explain in plain terms what the lender may do if you default, before you sign.",
    ],
    redFlags: [
      "Intellectual property included with no discussion at all.",
      "'All present and future obligations' language.",
      "No flexibility on permitted inventory locations.",
      "Obligations on third parties you have not spoken to.",
      "Remedies you have not had explained to you.",
      "A description so broad it forecloses your next financing.",
    ],
    beforeYouSign: [
      "A written list of every asset the description would actually cover.",
      "Every existing lender's paperwork, so you know what is already pledged.",
      "Your 3PL and co-packer agreements.",
      "Counsel's plain-English explanation of what happens on a default.",
    ],
    related: [
      "business-loan-agreement",
      "inventory-financing-agreement",
      "line-of-credit-agreement",
      "personal-guarantee",
      "equipment-financing-agreement",
      "three-pl-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This document decides which company assets — possibly including the brand and the formula — stand behind the debt, and what a lender may do about them is a legal question you need answered before signing.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "personal-guarantee",
    name: "Personal Guarantee",
    category: "financing",
    subcategory: "Security & guarantees",
    aliases: ["Guaranty", "Personal Guaranty", "Founder Guarantee", "Continuing Guarantee"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Founder (personally) + Lender, Lessor, Landlord, Co-Packer or Supplier",
    whenYouNeedIt:
      "Whenever a lender, equipment financier, landlord, co-packer or major supplier asks a founder to stand behind the company's obligations personally — which, for a young brand, is most of the time.",
    whatItDoes:
      "Makes you personally responsible for the company's obligation to that counterparty, on terms set out in a short separate document you sign in your own name rather than the company's.",
    whyItMatters:
      "This is the document that reaches past the company and into your own life, and it is usually signed at the end of a stack, late, at the point where everyone is tired and the production slot is already booked. The exposure does not automatically track the company: founders assume that closing the business, selling it, or simply leaving ends the guarantee, and checking that assumption before signing is precisely what counsel is for. Guarantees also accumulate quietly — a co-packer, an equipment financier, a landlord and a bank may each hold one, and no single document ever shows you the total.",
    majorTerms: [
      "What is guaranteed: this obligation only, or everything the company ever owes",
      "Whether the amount is capped",
      "Duration and whether it ends",
      "How you can be released",
      "Whether the counterparty must pursue the company first",
      "Whether a spouse is also asked to sign",
      "Whether it survives a sale of the company or your departure",
      "Notice obligations to you if the company defaults",
      "Whether costs and interest are included",
      "Whether it extends to renewals and increased limits",
    ],
    clausesToWatch: [
      {
        clause: "'Continuing' or 'all present and future obligations' language",
        why: "It covers debts the company has not taken on yet, so a guarantee given for one small credit line can end up standing behind a much larger facility three seasons later.",
      },
      {
        clause: "Absence of a cap",
        why: "Without a stated maximum, the exposure grows with the company's borrowing and you never signed for the larger number.",
      },
      {
        clause: "Absence of a release mechanism",
        why: "If nothing says how the guarantee ends, it does not end simply because you left, sold or wound the business down.",
      },
      {
        clause: "Whether the counterparty must deal with the company first",
        why: "It determines whether they can come straight to you before pursuing the company or the collateral.",
      },
      {
        clause: "Survival on a sale of the business",
        why: "Founders assume closing releases them, and if the document does not say so, that assumption is the whole risk.",
      },
      {
        clause: "Spousal signature requests",
        why: "It extends the exposure to a household rather than a person, and it should never be signed without counsel explaining what it reaches.",
      },
      {
        clause: "Extension to renewals and increases",
        why: "A facility renewed or increased without your separate approval can still sit behind the guarantee you signed years earlier.",
      },
    ],
    cpgIssues: [
      "Co-packers, ingredient suppliers, equipment lessors and cold-storage landlords all routinely ask a founder to guarantee, so a brand can carry four or five live guarantees before its first real raise.",
      "A guarantee given to secure one production run's credit line can end up standing behind a much larger facility later if it was written as continuing.",
      "Where two founders run a brand and only one guarantees, any obligation on the others to share that exposure has to be written down separately, because it does not exist otherwise.",
      "A guarantee given to a co-packer or a supplier shapes your negotiating position in a quality dispute, because the counterparty is also your personal creditor.",
      "Buyers and investors ask for a complete list of guarantees, and a forgotten one delays a deal at exactly the point where delay is most expensive.",
    ],
    founderMistakes: [
      "Signing a continuing guarantee for a small first credit line that later covered a much larger facility.",
      "Assuming a guarantee ended when the company was sold or wound down, without ever checking the document.",
      "Guaranteeing alone in a two-founder company with no reimbursement agreement between the founders.",
      "Not telling a co-founder or a spouse about a guarantee already signed.",
      "Never keeping a list, and discovering three live guarantees during diligence.",
    ],
    negotiationPoints: [
      "Ask for a stated dollar cap and a defined end date.",
      "Ask that it covers only the specific obligation in front of you, not future ones.",
      "Ask for a release trigger — a revenue level, a time period, a refinancing, or a sale.",
      "Ask that the counterparty must look to the company and its collateral first.",
      "Ask your co-founders to share the exposure in a written agreement between you.",
      "Ask counsel to explain exactly what you are signing, in plain terms, before you sign it.",
    ],
    redFlags: [
      "No cap and no end date.",
      "Language covering all present and future obligations.",
      "A request for a spouse's signature you have not discussed with counsel.",
      "No obligation to notify you if the company defaults.",
      "A guarantee presented at closing as a formality alongside everything else.",
      "A guarantee that continues to run after you leave the company.",
    ],
    beforeYouSign: [
      "A written list of every guarantee you have already given and to whom.",
      "The underlying agreement being guaranteed, read in full rather than summarised.",
      "Counsel's plain-English explanation of your exposure and how it ends.",
      "A written agreement with your co-founders about sharing the exposure.",
    ],
    related: [
      "security-agreement",
      "business-loan-agreement",
      "equipment-financing-agreement",
      "line-of-credit-agreement",
      "co-packer-agreement",
      "co-founder-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A guarantee is personal exposure that does not necessarily end when the company does, and no founder should sign one without counsel explaining its scope, its cap and how it is released.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default FINANCING_DOCS;
